import { Behavior, BehaviorConfig } from './Behavior';
import { Entity } from '../base/Entity';
import { PinBehavior } from './PinBehavior';
import { PlatformBehavior } from './PlatformBehavior';
import { LayerDepths } from '../../constants/enums';
import type { EntityFactory } from '../../systems/EntityFactory';

/**
 * Spawn configuration for child entities
 */
export interface SpawnConfig {
  /** Entity ID to spawn (from entities-overrides.json) */
  entityId: string;
  /** Y offset as ratio of parent's displayHeight (e.g., -0.85 = near top) */
  offsetY?: number;
  /** Pin to another spawned entity's image point. Format: "EntityId:ImagePoint" */
  pinTo?: string;
  /** Pin to parent entity's image point. The spawned entity will follow the parent. */
  pinToParent?: string;
  /** Custom depth (defaults to LayerDepths.Entities + spawn index) */
  depth?: number;
}

export interface SpawnChildrenConfig extends BehaviorConfig {
  /** Children to spawn on entity creation or when spawn() is called */
  spawnChildren?: SpawnConfig[];
  /** If true, auto-spawn on behavior creation. If false, call spawn() manually. */
  autoSpawn?: boolean;
}

/**
 * SpawnChildrenBehavior - Spawns child entities and manages their lifecycle
 *
 * Used by:
 * - CannonBase: spawns CannonCannon on creation (via autoSpawn or explicit spawn())
 * - TransformBehavior: delegates spawning after transformation completes
 *
 * Features:
 * - Spawns child entities via EntityFactory (reads from SSOT)
 * - Connects PinBehavior between spawned entities
 * - Handles parent death → children unpin and fall
 * - Cleans up children on destroy
 *
 * Config from SSOT (entities-overrides.json):
 * ```json
 * "cannonbase": {
 *   "behaviors": {
 *     "spawnChildren": {
 *       "spawnChildren": [
 *         { "entityId": "cannoncannon", "pinTo": "self:mount" }
 *       ]
 *     }
 *   }
 * }
 * ```
 */
export class SpawnChildrenBehavior extends Behavior {
  private spawnChildrenConfig: SpawnConfig[];
  private spawnedEntities: Entity[] = [];
  private hasSpawned: boolean = false;
  private autoSpawn: boolean;

  constructor(entity: Entity, config: SpawnChildrenConfig = {}) {
    super(entity, config);
    this.spawnChildrenConfig = config.spawnChildren ?? [];
    this.autoSpawn = config.autoSpawn ?? false;
  }

  /**
   * Called after entity is fully initialized (from EntityFactory)
   */
  spawn(): void {
    if (this.hasSpawned || this.spawnChildrenConfig.length === 0) return;
    this.hasSpawned = true;

    const entityFactory = this.scene.registry.get('entityFactory') as EntityFactory | undefined;
    if (!entityFactory) {
      console.error('[SpawnChildrenBehavior] EntityFactory not found in registry');
      return;
    }

    // Spawn each child entity in order
    for (let i = 0; i < this.spawnChildrenConfig.length; i++) {
      const spawned = this.spawnEntity(entityFactory, this.spawnChildrenConfig[i], i);
      if (spawned) {
        this.spawnedEntities.push(spawned);
      }
    }

    // Setup parent death listener
    this.setupParentDeathListener();
  }

  /**
   * Spawn a single entity based on config
   */
  private spawnEntity(
    entityFactory: EntityFactory,
    config: SpawnConfig,
    index: number
  ): Entity | null {
    // Calculate spawn position
    let spawnX = this.entity.x;
    let spawnY = this.entity.y;

    // Handle "self:imagepoint" syntax - pinTo references this entity's imagepoint
    if (config.pinTo?.startsWith('self:')) {
      const imagePointName = config.pinTo.substring(5); // Remove "self:"
      const point = this.entity.getImagePoint(imagePointName);
      if (point) {
        spawnX = point.x;
        spawnY = point.y;
      } else {
        console.warn(`[SpawnChildrenBehavior] Self imagepoint "${imagePointName}" not found`);
      }
    }
    // Pin to parent's imagepoint (follows parent as it animates/changes)
    else if (config.pinToParent) {
      const point = this.entity.getImagePoint(config.pinToParent);
      if (point) {
        spawnX = point.x;
        spawnY = point.y;
      } else {
        console.warn(`[SpawnChildrenBehavior] Parent imagepoint "${config.pinToParent}" not found`);
      }
    }
    // Apply Y offset (ratio of parent's height) - only if not pinning
    else if (config.offsetY !== undefined) {
      spawnY += config.offsetY * this.entity.displayHeight;
    } else if (index === 0) {
      // Default for first spawn (base): near the top of parent
      spawnY -= this.entity.displayHeight * 0.85;
    }

    // Check if we should get position from a previously spawned entity's image point
    if (config.pinTo && !config.pinTo.startsWith('self:') && this.spawnedEntities.length > 0) {
      const [prefabName, imagePointName] = config.pinTo.split(':');
      const targetEntity = this.findSpawnedByPrefab(prefabName);

      if (targetEntity) {
        const point = targetEntity.getImagePoint(imagePointName);
        if (point) {
          spawnX = point.x;
          spawnY = point.y;
        }
      }
    }

    // Create entity via factory (reads from SSOT)
    const spawned = entityFactory.createFromName(config.entityId, spawnX, spawnY);
    if (!spawned) {
      console.error(`[SpawnChildrenBehavior] Failed to create ${config.entityId}`);
      return null;
    }

    // Add to scene
    this.scene.add.existing(spawned);

    // Add physics
    if (this.scene.physics?.world) {
      this.scene.physics.add.existing(spawned);
      const body = spawned.body as Phaser.Physics.Arcade.Body;
      if (body) {
        body.setImmovable(true);
        body.setAllowGravity(false);

        // Apply hitbox from collision data
        const collisionManager = this.scene.registry.get('collisionManager') as
          | import('../../systems/DataManagerCollision').DataManagerCollision
          | undefined;
        if (collisionManager) {
          const entityData = spawned.config as Record<string, unknown>;
          const hasOriginOverride = entityData.originY !== undefined || entityData.originX !== undefined;
          const options = {
            useGroundedOrigin: false,
            skipOrigin: hasOriginOverride,
          };
          const applied = collisionManager.applyHitbox(spawned, config.entityId.toLowerCase(), options);
          if (!applied) {
            body.setSize(spawned.displayWidth, spawned.displayHeight);
            console.warn(`[SpawnChildrenBehavior] No collision data for ${config.entityId}, using displaySize`);
          }
        }
      }
    }

    // Set depth
    const depth = config.depth ?? (index === 0 ? LayerDepths.Entities - 1 : LayerDepths.Entities);
    spawned.setDepth(depth);

    // Play default animation if available
    const defaultAnimKey = `${config.entityId.toLowerCase()}_default`;
    if (this.scene.anims.exists(defaultAnimKey)) {
      spawned.play(defaultAnimKey);
    }

    // Add to entity group
    const entityGroup = this.scene.registry.get('entityGroup') as Phaser.GameObjects.Group | undefined;
    if (entityGroup) {
      entityGroup.add(spawned);
    }

    // Emit entitySpawned so CollisionSystem registers this entity
    const body = spawned.body as Phaser.Physics.Arcade.Body | null;
    console.log(`[SpawnChildrenBehavior] Spawned ${config.entityId}`, {
      allegiance: spawned.allegiance,
      hasBody: !!body,
      position: { x: Math.round(spawned.x), y: Math.round(spawned.y) },
    });
    this.scene.events.emit('entitySpawned', spawned);

    // Connect PinBehavior to parent's imagepoint if using "self:imagepoint" syntax
    if (config.pinTo?.startsWith('self:')) {
      const imagePointName = config.pinTo.substring(5);
      const pinBehavior = spawned.getBehavior(PinBehavior);
      if (pinBehavior) {
        pinBehavior.pinTo(this.entity, imagePointName, false);
      } else {
        console.warn(`[SpawnChildrenBehavior] ${config.entityId} has pinTo but no PinBehavior`);
      }
    }
    // Connect PinBehavior to parent if specified
    else if (config.pinToParent) {
      const pinBehavior = spawned.getBehavior(PinBehavior);
      if (pinBehavior) {
        pinBehavior.pinTo(this.entity, config.pinToParent, false);
      } else {
        console.warn(`[SpawnChildrenBehavior] ${config.entityId} has pinToParent but no PinBehavior`);
      }
    }
    // Connect PinBehavior to other spawned entity if specified
    else if (config.pinTo && this.spawnedEntities.length > 0) {
      const [prefabName, imagePointName] = config.pinTo.split(':');
      const targetEntity = this.findSpawnedByPrefab(prefabName);

      if (targetEntity) {
        const pinBehavior = spawned.getBehavior(PinBehavior);
        if (pinBehavior) {
          pinBehavior.pinTo(targetEntity, imagePointName, false);
        }
      }
    }

    // Disable PlatformBehavior - spawned entity stays fixed until parent dies
    const platformBehavior = spawned.getBehavior(PlatformBehavior);
    if (platformBehavior) {
      platformBehavior.enabled = false;
    }

    // Listen for death to manage cleanup
    // Using .on() instead of .once() because .once() would be consumed if any other
    // specialChanged state is emitted before 'death' (e.g., 'none')
    spawned.on('specialChanged', (state: string) => {
      if (state === 'death') {
        this.onSpawnedEntityDeath(spawned, index);
      }
    });

    return spawned;
  }

  /**
   * Setup listener for parent death to destroy all spawned children
   */
  private setupParentDeathListener(): void {
    // Using .on() instead of .once() because .once() would be consumed if any other
    // specialChanged state is emitted before 'death' (e.g., 'none')
    this.entity.on('specialChanged', (state: string) => {
      if (state === 'death') {
        // When parent dies, destroy all spawned children (including CannonCannon)
        this.destroyAllSpawned();
      }
    });
  }

  /**
   * Find a spawned entity by prefab/texture name (case-insensitive)
   */
  private findSpawnedByPrefab(prefabName: string): Entity | null {
    const lowerName = prefabName.toLowerCase();
    return this.spawnedEntities.find(e => {
      const textureKey = e.texture?.key?.toLowerCase() ?? '';
      return textureKey === lowerName;
    }) ?? null;
  }

  /**
   * Handle death of a spawned entity
   */
  private onSpawnedEntityDeath(entity: Entity, index: number): void {
    const idx = this.spawnedEntities.indexOf(entity);
    if (idx !== -1) {
      this.spawnedEntities.splice(idx, 1);
    }

    // If the first spawned entity (base) is destroyed, destroy all other children
    if (index === 0) {
      this.destroyAllSpawned();
    }
  }

  /**
   * Destroy all spawned entities
   */
  private destroyAllSpawned(): void {
    for (const entity of [...this.spawnedEntities]) {
      if (entity.active) {
        entity.destroy();
      }
    }
    this.spawnedEntities = [];
  }

  /**
   * Cleanup when behavior is destroyed
   */
  override destroy(): void {
    this.destroyAllSpawned();
    super.destroy();
  }

  // =========================================================================
  // Public API
  // =========================================================================

  /**
   * Get all spawned entities
   */
  getSpawnedEntities(): Entity[] {
    return [...this.spawnedEntities];
  }

  /**
   * Check if spawn has been called
   */
  hasSpawnedChildren(): boolean {
    return this.hasSpawned;
  }

  /**
   * Check if any spawned children are still active
   */
  hasActiveChildren(): boolean {
    return this.spawnedEntities.length > 0 && this.spawnedEntities.some(e => e.active);
  }

  /**
   * Get a spawned entity by index
   */
  getSpawnedAt(index: number): Entity | null {
    return this.spawnedEntities[index] ?? null;
  }
}
